import { css } from '@emotion/react';

/* ───────────────────────── Main layout ───────────────────────── */
export const budgetsManagementCSS = () => css`
    font-family: 'Neue Haas Grotesk Display Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #0f172a;

    .bm-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        margin-bottom: 1rem;
        flex-wrap: wrap;
    }

    .bm-title {
        font-size: 1.05rem;
        font-weight: 700;
        color: #0f172a;
        letter-spacing: 0.02em;
        margin: 0;
    }

    .bm-subtitle {
        font-size: 0.78rem;
        color: #64748b;
        margin: 0.15rem 0 0;
    }

    /* ─── KPI chips ─── */
    .bm-kpis {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 0.6rem;
        margin: 0 0 1rem;
    }

    @media (max-width: 520px) {
        .bm-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    }

    .bm-kpi {
        position: relative;
        display: flex;
        align-items: center;
        gap: 0.65rem;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 14px;
        padding: 0.8rem 0.85rem;
        cursor: pointer;
        transition: transform 0.18s, border-color 0.18s, box-shadow 0.18s, background 0.18s;
        text-align: left;
        font-family: inherit;
        color: #0f172a;
        overflow: hidden;
        box-shadow: 2px 2px 11px rgba(33, 88, 140, 0.04);
    }
    .bm-kpi:hover {
        transform: translateY(-2px);
        box-shadow: inset 3px 0 0 transparent, 0 8px 18px rgba(33, 88, 140, 0.08);
    }

    .bm-kpi-icon {
        flex-shrink: 0;
        width: 36px;
        height: 36px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
    }

    .bm-kpi-body {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 0;
        gap: 2px;
    }
    .bm-kpi-label {
        font-size: 0.62rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.07em;
        color: #64748b;
        line-height: 1.1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .bm-kpi-value {
        font-size: 1.5rem;
        font-weight: 700;
        line-height: 1;
        letter-spacing: -0.015em;
        color: #0f172a;
        margin-top: 2px;
    }

    @media (max-width: 520px) {
        .bm-kpi-icon { width: 30px; height: 30px; font-size: 0.85rem; }
        .bm-kpi-value { font-size: 1.25rem; }
        .bm-kpi { padding: 0.65rem 0.7rem; gap: 0.5rem; }
    }

    /* ─── KPI tones ─── */
    .bm-kpi.tone-all:hover { border-color: #204B7B; box-shadow: inset 3px 0 0 #204B7B, 0 8px 18px rgba(33,88,140,0.08); }
    .bm-kpi.tone-all.active { border-color: #204B7B; background: #eef2f7; box-shadow: inset 3px 0 0 #204B7B, 0 6px 16px #204B7B26; }
    .bm-kpi.tone-all .bm-kpi-icon { color: #204B7B; background: #eef2f7; }
    .bm-kpi.tone-all.active .bm-kpi-icon { background: #ffffff; }

    .bm-kpi.tone-pei:hover { border-color: #00BA6E; box-shadow: inset 3px 0 0 #00BA6E, 0 8px 18px rgba(33,88,140,0.08); }
    .bm-kpi.tone-pei.active { border-color: #00BA6E; background: #e6f9f1; box-shadow: inset 3px 0 0 #00BA6E, 0 6px 16px #00BA6E26; }
    .bm-kpi.tone-pei .bm-kpi-icon { color: #00BA6E; background: #e6f9f1; }
    .bm-kpi.tone-pei.active .bm-kpi-icon { background: #ffffff; }

    .bm-kpi.tone-trim:hover { border-color: #34C6F1; box-shadow: inset 3px 0 0 #34C6F1, 0 8px 18px rgba(33,88,140,0.08); }
    .bm-kpi.tone-trim.active { border-color: #34C6F1; background: #e3f5fb; box-shadow: inset 3px 0 0 #34C6F1, 0 6px 16px #34C6F126; }
    .bm-kpi.tone-trim .bm-kpi-icon { color: #34C6F1; background: #e3f5fb; }
    .bm-kpi.tone-trim.active .bm-kpi-icon { background: #ffffff; }

    /* ─── Skeleton placeholders ─── */
    .bm-kpi-skeleton {
        cursor: default;
        pointer-events: none;
    }
    .bm-kpi-skeleton:hover {
        transform: none;
        box-shadow: 2px 2px 11px rgba(33, 88, 140, 0.04);
        border-color: #e2e8f0;
    }
    .bm-row-skeleton { cursor: default !important; }
    .bm-row-skeleton:hover { background: transparent !important; }
    .bm-row-skeleton td { padding: 14px 20px; }

    /* ─── Toolbar ─── */
    .bm-toolbar {
        display: flex;
        gap: 0.5rem;
        align-items: center;
        margin-bottom: 0.85rem;
        flex-wrap: wrap;
    }

    .bm-search {
        flex: 1;
        min-width: 240px;
        max-width: 360px;
        position: relative;
    }
    .bm-search-icon {
        position: absolute;
        right: 1.1rem;
        top: 50%;
        transform: translateY(-50%);
        color: #94a3b8;
        font-size: 0.85rem;
        pointer-events: none;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .bm-search input {
        width: 100%;
        padding: 0.55rem 2.2rem 0.55rem 1rem;
        border-radius: 999px;
        border: 1px solid #e2e8f0;
        background: #ffffff;
        color: #0f172a;
        font-size: 0.85rem;
        font-family: inherit;
        outline: none;
        transition: border-color 0.18s, box-shadow 0.18s;
    }
    .bm-search input:focus {
        border-color: #34C6F1;
        box-shadow: 0 0 0 3px rgba(52, 198, 241, 0.18);
    }

    /* ─── Table ─── */
    .bm-table-card {
        background: #fff;
        border-radius: 14px;
        box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
        border: 1px solid #e2e8f0;
        overflow: hidden;
    }

    .bm-table-scroll { overflow-x: auto; }

    .bm-table {
        width: 100%;
        border-collapse: collapse;
        text-align: left;
    }

    .bm-table thead tr {
        background: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
    }

    .bm-table th {
        padding: 14px 20px;
        font-size: 0.7rem;
        font-weight: 700;
        color: #475569;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        white-space: nowrap;
    }

    .bm-table tbody tr {
        transition: background-color 0.12s;
        cursor: pointer;
    }

    .bm-table tbody tr:hover {
        background: rgba(248, 250, 252, 0.85);
    }

    .bm-table tbody tr:not(:last-child) {
        border-bottom: 1px solid #f1f5f9;
    }

    .bm-table td {
        padding: 12px 20px;
        font-size: 0.82rem;
        color: #333333;
        vertical-align: middle;
    }

    .bm-table td.bm-td-currency {
        font-variant-numeric: tabular-nums;
        font-weight: 600;
    }

    /* ─── Pagination ─── */
    .bm-pagination {
        padding: 12px 20px;
        border-top: 1px solid #e2e8f0;
        background: #f8fafc;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .bm-empty {
        padding: 3rem 1rem;
        text-align: center;
        color: #94a3b8;
        font-size: 0.88rem;
    }

    .bm-empty i {
        display: block;
        font-size: 1.6rem;
        margin-bottom: 0.5rem;
        opacity: 0.55;
    }

    @media (max-width: 650px) {
        .bm-header { flex-direction: column; align-items: flex-start; }
        .bm-search { max-width: 100%; }
        .bm-table th, .bm-table td { padding: 10px 12px; }
    }
`;

/* ───────────────────────── Source chip ───────────────────────── */
export const sourceChipCSS = (source) => {
    const palette = {
        pei:  { bg: '#00BA6E', color: '#ffffff' },
        trim: { bg: '#34C6F1', color: '#0f172a' },
    };
    const c = palette[source] ?? { bg: '#f1f5f9', color: '#64748b' };
    return css`
        display: inline-flex;
        align-items: center;
        background: ${c.bg};
        color: ${c.color};
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        padding: 3px 10px;
        border-radius: 999px;
        white-space: nowrap;
        text-transform: uppercase;
    `;
};

/* Chips del detalle: plan (financiero/puro), placa y seguro manual. */
export const resumenChipCSS = (tono) => {
    const palette = {
        financiero: { bg: '#dbeafe', color: '#1e40af' },
        puro:       { bg: '#d1fae5', color: '#065f46' },
        cotizador3: { bg: '#ede9fe', color: '#5b21b6' },
        placa:      { bg: '#f1f5f9', color: '#334155' },
        manual:     { bg: '#fef3c7', color: '#92400e' },
    };
    const c = palette[tono] ?? palette.placa;
    return css`
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background: ${c.bg};
        color: ${c.color};
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        padding: 3px 10px;
        border-radius: 999px;
        white-space: nowrap;
        text-transform: uppercase;

        i { font-size: 0.65rem; }
    `;
};

/* ───────────────────────── Budget Detail Modal ───────────────────────── */
export const budgetDetailModalCSS = () => css`
    .bdm-header {
        display: flex;
        align-items: center;
        gap: 0.85rem;
        padding-bottom: 0.85rem;
        border-bottom: 1px solid #e2e8f0;
        margin-bottom: 1rem;
    }

    .bdm-avatar {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: linear-gradient(135deg, #204B7B 0%, #00BA6E 100%);
        color: #ffffff;
        font-weight: 700;
        font-size: 0.9rem;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        letter-spacing: 0.02em;
    }

    .bdm-identity {
        flex: 1;
        min-width: 0;
    }

    .bdm-name {
        font-size: 0.95rem;
        font-weight: 700;
        color: #0f172a;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .bdm-email {
        font-size: 0.78rem;
        color: #64748b;
        margin: 0.1rem 0 0;
    }

    .bdm-chips {
        display: flex;
        gap: 0.4rem;
        margin-top: 0.45rem;
        flex-wrap: wrap;
    }

    .bdm-section {
        margin-bottom: 1.2rem;
    }

    .bdm-section-title {
        font-size: 0.78rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #475569;
        margin: 0 0 0.6rem;
        display: flex;
        align-items: center;
        gap: 0.4rem;
    }

    .bdm-section-title i {
        font-size: 0.85rem;
        color: #94a3b8;
    }

    .bdm-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.85rem 1rem;
    }

    @media (max-width: 650px) {
        .bdm-grid { grid-template-columns: 1fr; }
    }

    .bdm-field {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
    }

    .bdm-label {
        font-size: 0.72rem;
        font-weight: 600;
        color: #475569;
        letter-spacing: 0.03em;
    }

    .bdm-value {
        font-size: 0.85rem;
        color: #0f172a;
        word-break: break-word;
    }

    .bdm-value--muted { color: #94a3b8; }

    .bdm-value--currency {
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: #0f172a;
    }

    .bdm-divider {
        height: 1px;
        background: #e2e8f0;
        margin: 0.6rem 0 1rem;
    }

    .bdm-vehicle-card {
        display: flex;
        align-items: center;
        gap: 0.85rem;
        padding: 0.85rem;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        margin-bottom: 1rem;
    }

    .bdm-vehicle-icon {
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: linear-gradient(135deg, #204B7B, #34C6F1);
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.1rem;
        flex-shrink: 0;
    }

    .bdm-vehicle-info {
        flex: 1;
        min-width: 0;
    }

    .bdm-vehicle-name {
        font-size: 0.88rem;
        font-weight: 700;
        color: #0f172a;
    }

    .bdm-vehicle-desc {
        font-size: 0.74rem;
        color: #64748b;
        margin-top: 0.1rem;
    }

    @media (max-width: 650px) {
        .bdm-header { flex-wrap: wrap; }
    }

    .bdm-ai-prompt {
        display: flex;
        gap: 0.65rem;
        align-items: flex-start;
        background: linear-gradient(135deg, #f0f7ff 0%, #f5f0ff 100%);
        border: 1px solid #c7d9f5;
        border-radius: 12px;
        padding: 0.75rem 0.9rem;
        margin-bottom: 1rem;
    }

    .bdm-ai-prompt-icon {
        font-size: 1rem;
        flex-shrink: 0;
        margin-top: 1px;
    }

    .bdm-ai-prompt-body {
        flex: 1;
        min-width: 0;
    }

    .bdm-ai-prompt-label {
        font-size: 0.68rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #5b7fa6;
        margin-bottom: 0.3rem;
    }

    .bdm-ai-prompt-text {
        font-size: 0.82rem;
        color: #1e3a5f;
        font-style: italic;
        line-height: 1.5;
        word-break: break-word;
    }

    /* Pie con las acciones: pegado abajo aunque el detalle haga scroll.
       Los -20px compensan el padding del contenido del Dialog. */
    .bdm-footer {
        position: sticky;
        bottom: -20px;
        z-index: 1;
        display: flex;
        justify-content: flex-end;
        gap: 0.6rem;
        flex-wrap: wrap;
        margin: 0.4rem -20px -20px;
        padding: 0.85rem 20px 1rem;
        background: #fefefe;
        border-top: 1px solid #e2e8f0;
        border-radius: 0 0 0.8rem 0.8rem;
    }

    @media (max-width: 650px) {
        .bdm-footer .fk-btn { flex: 1; }
    }
`;

/* ───────────────────────── Recotizar (diálogo) ───────────────────────── */
export const recotizarDialogCSS = () => css`
    .rbd-banner {
        display: flex;
        align-items: flex-start;
        gap: 0.55rem;
        padding: 0.7rem 0.9rem;
        border-radius: 10px;
        font-size: 0.82rem;
        line-height: 1.45;
        margin-bottom: 0.75rem;
    }

    .rbd-banner i { margin-top: 2px; }

    .rbd-banner--info {
        background: #ECF8FE;
        border: 1px solid rgba(50, 184, 223, 0.30);
        color: #075985;
    }

    .rbd-banner--warn {
        background: #fffbeb;
        border: 1px solid #fde68a;
        color: #92400e;
    }

    .rbd-estado {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.85rem;
        padding: 3rem 1rem;
        text-align: center;
        color: #64748b;
        font-size: 0.9rem;
    }

    .rbd-estado > i.pi { font-size: 1.6rem; }
    .rbd-estado--error > i.pi { color: #ef4444; }
    .rbd-estado--bloqueo > i.pi { color: #d97706; }

    .rbd-estado p {
        margin: 0;
        max-width: 460px;
        color: #334155;
        line-height: 1.5;
    }

    .rbd-acciones {
        display: flex;
        gap: 0.5rem;
        justify-content: center;
        flex-wrap: wrap;
    }
`;

/* ───────────────────────── Seguro manual (tarjeta) ─────────────────────────
   Va dentro de CarDataForm, debajo de "Seguro", con la clase FormCard de ahí. */
export const seguroManualCardCSS = () => css`
    border: 1px solid rgba(255, 108, 14, 0.30);
    transition: border-color 0.2s, box-shadow 0.2s;

    &.smc--pendiente {
        border-color: #FF6C0E;
        box-shadow: 0 0 0 3px rgba(255, 108, 14, 0.18);
    }

    .smc-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 0.75rem;
        flex-wrap: wrap;
    }

    .smc-title {
        margin: 0.2rem 0 0;
        color: #21588C;
        font-size: 1.5rem;
    }

    .smc-chips {
        display: flex;
        gap: 0.4rem;
        flex-wrap: wrap;
    }

    .smc-chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 0.68rem;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        padding: 3px 10px;
        border-radius: 999px;
        background: #FFF7ED;
        color: #C2410C;
        border: 1px solid rgba(255, 108, 14, 0.30);
        white-space: nowrap;
    }

    .smc-chip--ok {
        background: #e6f9f1;
        color: #047857;
        border-color: rgba(0, 186, 110, 0.35);
    }

    .smc-help {
        margin: 0.45rem 0 0;
        color: #67808f;
        line-height: 1.55;
        font-size: 0.92rem;
        max-width: 620px;
    }

    .smc-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.85rem 1rem;
    }

    .smc-primas {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 0.85rem 1rem;
    }

    .smc-plazo {
        display: flex;
        flex-direction: column;
        gap: 0.6rem;
        min-width: 0;
    }

    .smc-sin-folio { align-self: flex-start; }

    @media (max-width: 650px) {
        .smc-grid, .smc-primas { grid-template-columns: 1fr; }
    }

    .p-inputnumber { width: 100%; }

    .smc-error {
        margin: 0;
        color: #dc2626;
        font-size: 0.78rem;
        font-weight: 500;
    }

    .smc-acciones {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.6rem;
        flex-wrap: wrap;
    }

    .smc-estado {
        margin-right: auto;
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        font-size: 0.8rem;
        font-weight: 600;
        color: #047857;
    }

    .smc-estado--pendiente { color: #C2410C; }

    /* "Usar este seguro" en naranja Peicar: el verde es de COTIZAR, que va después. */
    .smc-acciones .smc-usar.fk-btn {
        background-color: #FF6C0E;
        color: #ffffff;
        border: none;
    }
    .smc-acciones .smc-usar.fk-btn:hover:not(:disabled) { background-color: #E85D00; }
    .smc-acciones .smc-usar.fk-btn:focus-visible { box-shadow: 0 0 0 2px rgba(255, 108, 14, 0.35); }
`;

export const budgetDetailDialogStyle = (isMobile) => isMobile ? ({
    width: '100vw',
    height: '90vh',
    margin: 0,
    borderRadius: '0.8rem 0.8rem 0 0',
    position: 'fixed',
    bottom: 0,
}) : ({
    width: '680px',
    maxWidth: '94vw',
    borderRadius: '0.8rem',
});
